import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  playbookItems as playbookNav,
  metricsItems as metricsNav,
  platformItems as platformNav,
} from '@/lib/navigation'
import { Separator } from '@/components/ui/separator'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import {
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  Home,
  LogOut,
  Monitor,
  Moon,
  MoreVertical,
  Search,
  Sun,
  X,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { logout } from '../../modules/authentication'
import { selectCurrentUser } from '../../modules/common'
import { useAppDispatch, useAppSelector } from '../../modules/core'
import { openCommandPalette } from '../CommandPalette'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'

// Use shared navigation items from navigation.ts (single source of truth)
const playbookItems = playbookNav.items
const metricsItems = metricsNav.items
const platformItems = platformNav.items

interface SidebarProps {
  className?: string
  isCollapsed?: boolean
  onToggleCollapse?: () => void
  /** Callback to close mobile sidebar */
  onCloseMobile?: () => void
  /** Whether mobile sidebar is currently open */
  isMobileSidebarOpen?: boolean
}

// Collapsible section component
function CollapsibleSection({
  title,
  icon: Icon,
  items,
  isCollapsed,
  isSidebarCollapsed,
  onToggle,
  location,
}: {
  title: string
  icon: React.ElementType
  items: { title: string; href: string; icon: React.ElementType; separator?: boolean }[]
  isCollapsed: boolean
  isSidebarCollapsed: boolean
  onToggle: () => void
  location: ReturnType<typeof useLocation>
}) {
  const isAnyChildActive = items.some(item => location.pathname.startsWith(item.href))

  // Auto-expand if a child is active (only on initial mount or navigation)
  useEffect(() => {
    if (isAnyChildActive && isCollapsed) {
      onToggle()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isAnyChildActive])

  if (isSidebarCollapsed) {
    // When sidebar is collapsed, show one top-level icon that opens a popover with sub-items
    return (
      <Popover>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button
                className={cn(
                  'flex w-full items-center justify-center rounded-lg px-2 py-2 text-sm font-medium transition-colors hover:bg-muted hover:text-foreground',
                  isAnyChildActive ? 'bg-muted text-foreground' : 'text-muted-foreground'
                )}
              >
                <Icon className="h-4 w-4 shrink-0" strokeWidth={2.5} />
              </button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={10}>
            {title}
          </TooltipContent>
        </Tooltip>
        <PopoverContent side="right" align="start" sideOffset={8} className="w-48 p-1">
          <div className="space-y-0.5">
            {items.map(item => {
              const isActive = location.pathname.startsWith(item.href)
              return (
                <div key={item.href}>
                  {item.separator && <div className="my-1 h-px bg-border" />}
                  <Link
                    to={item.href}
                    className={cn(
                      'flex items-center rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors hover:bg-muted hover:text-foreground',
                      isActive ? 'bg-muted text-foreground' : 'text-muted-foreground'
                    )}
                  >
                    <item.icon className="mr-2.5 h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />
                    <span>{item.title}</span>
                  </Link>
                </div>
              )
            })}
          </div>
        </PopoverContent>
      </Popover>
    )
  }

  return (
    <div className="space-y-1">
      <button
        onClick={onToggle}
        className={cn(
          'flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm font-medium transition-colors hover:bg-muted hover:text-foreground',
          isAnyChildActive ? 'text-foreground' : 'text-muted-foreground'
        )}
      >
        <div className="flex items-center">
          <Icon className="mr-3 h-4 w-4 shrink-0" strokeWidth={2.5} />
          <span>{title}</span>
        </div>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 transition-transform duration-200',
            isCollapsed ? '-rotate-90' : ''
          )}
          strokeWidth={2.5}
        />
      </button>
      {!isCollapsed && (
        <div className="ml-4 space-y-1 border-l border-border pl-3">
          {items.map(item => {
            const isActive = location.pathname.startsWith(item.href)
            return (
              <div key={item.href}>
                {item.separator && <div className="my-1.5 h-px bg-border" />}
                <Link
                  to={item.href}
                  className={cn(
                    'flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors hover:bg-muted hover:text-foreground',
                    isActive ? 'bg-muted text-foreground' : 'text-muted-foreground'
                  )}
                >
                  <item.icon className="mr-3 h-4 w-4 shrink-0" strokeWidth={2.5} />
                  <span>{item.title}</span>
                </Link>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export function Sidebar({
  className,
  isCollapsed = false,
  onToggleCollapse,
  onCloseMobile,
  isMobileSidebarOpen,
}: SidebarProps) {
  const location = useLocation()
  const currentUser = useAppSelector(selectCurrentUser)
  const dispatch = useAppDispatch()

  // Accordion state - only one section can be expanded at a time (null = all collapsed)
  const [expandedSection, setExpandedSection] = useState<
    'playbook' | 'metrics' | 'platform' | null
  >(null)

  const toggleSection = (section: 'playbook' | 'metrics' | 'platform') => {
    setExpandedSection(prev => (prev === section ? null : section))
  }

  // Theme state: 'light' | 'dark' | 'system'
  const [theme, setTheme] = useState<'light' | 'dark' | 'system'>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('theme')
      if (stored === 'light' || stored === 'dark' || stored === 'system') {
        return stored
      }
    }
    return 'system'
  })

  useEffect(() => {
    const root = document.documentElement
    localStorage.setItem('theme', theme)

    const applyTheme = (isDark: boolean) => {
      if (isDark) {
        root.classList.add('dark')
      } else {
        root.classList.remove('dark')
      }
    }

    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      applyTheme(mediaQuery.matches)

      const handler = (e: MediaQueryListEvent) => applyTheme(e.matches)
      mediaQuery.addEventListener('change', handler)
      return () => mediaQuery.removeEventListener('change', handler)
    } else {
      applyTheme(theme === 'dark')
    }
  }, [theme])

  const getThemeIcon = () => {
    if (theme === 'light') return <Sun className="h-4 w-4" strokeWidth={2.5} />
    if (theme === 'dark') return <Moon className="h-4 w-4" strokeWidth={2.5} />
    return <Monitor className="h-4 w-4" strokeWidth={2.5} />
  }

  const getThemeLabel = () => {
    if (theme === 'light') return 'Light'
    if (theme === 'dark') return 'Dark'
    return 'Auto'
  }

  const handleLogout = () => {
    dispatch(logout())
  }

  return (
    <TooltipProvider delayDuration={0}>
      <div className={cn('flex h-full flex-col border-r bg-background', className)}>
        {/* Header */}
        <div
          className={cn(
            'relative flex h-16 items-center justify-between',
            isCollapsed ? 'px-2' : 'px-4'
          )}
        >
          <Link to="/" className={cn('flex items-center', isCollapsed ? '' : 'space-x-2')}>
            <div
              className={cn(
                'flex shrink-0 items-center justify-center overflow-hidden rounded-lg',
                isCollapsed ? 'h-10 w-10' : 'h-12 w-12'
              )}
            >
              <img
                src="/icon.png"
                alt="BirdBrain Logo"
                className={cn(isCollapsed ? 'h-10 w-10' : 'h-12 w-12', 'object-cover')}
              />
            </div>
            {!isCollapsed && (
              <div>
                <div className="font-semibold text-foreground">BirdBrain</div>
                <div className="text-xs text-muted-foreground">Taka's command center</div>
              </div>
            )}
          </Link>

          {/* Mobile close button */}
          {onCloseMobile && isMobileSidebarOpen && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onCloseMobile}
              className="touch-target h-10 w-10 md:hidden"
              aria-label="Close sidebar"
            >
              <X className="h-5 w-5" />
            </Button>
          )}

          {/* Collapse button on sidebar border (desktop only) */}
          {onToggleCollapse && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size={null}
                  onClick={onToggleCollapse}
                  className="absolute -right-3 top-1/2 z-10 hidden h-6 w-6 -translate-y-1/2 rounded-full border bg-background p-0 shadow-md hover:bg-muted md:flex"
                >
                  {isCollapsed ? (
                    <ChevronsRight className="h-3 w-3" strokeWidth={2.5} />
                  ) : (
                    <ChevronsLeft className="h-3 w-3" strokeWidth={2.5} />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={8}>
                {isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        <Separator />

        {/* Search Button */}
        <div className={cn(isCollapsed ? 'p-2' : 'px-4 pt-4')}>
          {isCollapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={openCommandPalette}
                  className="flex w-full items-center justify-center rounded-lg px-2 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <Search className="h-4 w-4 shrink-0" strokeWidth={2.5} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={10}>
                Search (⌘K)
              </TooltipContent>
            </Tooltip>
          ) : (
            <button
              onClick={openCommandPalette}
              className="flex w-full items-center gap-2 rounded-lg border border-input bg-background px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <Search className="h-4 w-4 shrink-0" strokeWidth={2.5} />
              <span className="flex-1 text-left">Search...</span>
              <kbd className="pointer-events-none hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground sm:inline-flex">
                <span className="text-xs">⌘</span>K
              </kbd>
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav
          className={cn(
            'flex-1 space-y-2 overflow-y-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
            isCollapsed ? 'p-2' : 'p-4'
          )}
        >
          {/* Home Link */}
          {isCollapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to="/"
                  className={cn(
                    'flex items-center justify-center rounded-lg px-2 py-2 text-sm font-medium transition-colors hover:bg-muted hover:text-foreground',
                    location.pathname === '/' ? 'bg-muted text-foreground' : 'text-muted-foreground'
                  )}
                >
                  <Home className="h-4 w-4 shrink-0" strokeWidth={2.5} />
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={10}>
                Home
              </TooltipContent>
            </Tooltip>
          ) : (
            <Link
              to="/"
              className={cn(
                'flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors hover:bg-muted hover:text-foreground',
                location.pathname === '/' ? 'bg-muted text-foreground' : 'text-muted-foreground'
              )}
            >
              <Home className="mr-3 h-4 w-4 shrink-0" strokeWidth={2.5} />
              <span>Home</span>
            </Link>
          )}

          {/* Playbook Section */}
          <CollapsibleSection
            title={playbookNav.title}
            icon={playbookNav.icon}
            items={playbookItems}
            isCollapsed={expandedSection !== 'playbook'}
            isSidebarCollapsed={isCollapsed}
            onToggle={() => toggleSection('playbook')}
            location={location}
          />

          {/* Metrics Section */}
          <CollapsibleSection
            title={metricsNav.title}
            icon={metricsNav.icon}
            items={metricsItems}
            isCollapsed={expandedSection !== 'metrics'}
            isSidebarCollapsed={isCollapsed}
            onToggle={() => toggleSection('metrics')}
            location={location}
          />

          {/* Platform Section */}
          <CollapsibleSection
            title={platformNav.title}
            icon={platformNav.icon}
            items={platformItems}
            isCollapsed={expandedSection !== 'platform'}
            isSidebarCollapsed={isCollapsed}
            onToggle={() => toggleSection('platform')}
            location={location}
          />
        </nav>

        {/* Theme Toggle */}
        <Separator />
        <div className={cn('p-2', isCollapsed ? 'flex flex-col items-center' : '')}>
          {isCollapsed ? (
            <Select
              value={theme}
              onValueChange={(value: 'light' | 'dark' | 'system') => setTheme(value)}
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <SelectTrigger className="h-10 w-10 justify-center border-0 bg-transparent p-0 text-muted-foreground hover:bg-muted hover:text-foreground focus:ring-0 focus:ring-offset-0 [&>svg:last-child]:hidden">
                    <SelectValue>{getThemeIcon()}</SelectValue>
                  </SelectTrigger>
                </TooltipTrigger>
                <TooltipContent side="right" sideOffset={10}>
                  Theme: {getThemeLabel()}
                </TooltipContent>
              </Tooltip>
              <SelectContent side="right" align="start">
                <SelectItem value="dark">
                  <div className="flex items-center gap-2">
                    <Moon className="h-4 w-4" strokeWidth={2.5} />
                    Dark
                  </div>
                </SelectItem>
                <SelectItem value="system">
                  <div className="flex items-center gap-2">
                    <Monitor className="h-4 w-4" strokeWidth={2.5} />
                    Auto
                  </div>
                </SelectItem>
                <SelectItem value="light">
                  <div className="flex items-center gap-2">
                    <Sun className="h-4 w-4" strokeWidth={2.5} />
                    Light
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <div className="flex w-full items-center rounded-lg border bg-muted/50 p-1">
              <button
                onClick={() => setTheme('dark')}
                className={cn(
                  'flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
                  theme === 'dark'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                <Moon className="h-3.5 w-3.5" strokeWidth={2.5} />
                Dark
              </button>
              <button
                onClick={() => setTheme('system')}
                className={cn(
                  'flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
                  theme === 'system'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                <Monitor className="h-3.5 w-3.5" strokeWidth={2.5} />
                Auto
              </button>
              <button
                onClick={() => setTheme('light')}
                className={cn(
                  'flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
                  theme === 'light'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                <Sun className="h-3.5 w-3.5" strokeWidth={2.5} />
                Light
              </button>
            </div>
          )}
        </div>

        {/* User Profile */}
        <Separator />
        <div className={cn(isCollapsed ? 'p-2' : 'p-4')}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              {isCollapsed ? (
                <div className="flex cursor-pointer items-center justify-center rounded-lg p-2 hover:bg-muted hover:text-foreground">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={currentUser?.avatar?.publicUrl} />
                    <AvatarFallback>{currentUser?.name?.charAt(0) || 'A'}</AvatarFallback>
                  </Avatar>
                </div>
              ) : (
                <div className="-m-2 flex cursor-pointer items-center space-x-3 rounded-lg p-2 hover:bg-muted hover:text-foreground">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={currentUser?.avatar?.publicUrl} />
                    <AvatarFallback>{currentUser?.name?.charAt(0) || 'A'}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">
                      {currentUser?.name || currentUser?.email?.split('@')[0] || 'Admin User'}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {currentUser?.email || ''}
                    </p>
                  </div>
                  <MoreVertical className="h-4 w-4 text-muted-foreground" strokeWidth={2.5} />
                </div>
              )}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600">
                <LogOut className="mr-2 h-4 w-4" strokeWidth={2.5} />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </TooltipProvider>
  )
}
